import React, { useState } from 'react';
import { Form, Input, Select } from '@rocketseat/unform';
import * as Yup from 'yup';
import PropTypes from 'prop-types';

import { toast } from 'react-toastify';
import { Container, GroupHeader, Group } from './styles';
import api from '~/services/api';
import history from '~/services/history';

const states = [
  { id: 'AL', title: 'AL' },
  { id: 'AK', title: 'AK' },
  { id: 'AS', title: 'AS' },
  { id: 'AZ', title: 'AZ' },
  { id: 'AR', title: 'AR' },
  { id: 'CA', title: 'CA' },
  { id: 'CO', title: 'CO' },
  { id: 'CT', title: 'CT' },
  { id: 'DE', title: 'DE' },
  { id: 'DC', title: 'DC' },
  { id: 'FM', title: 'FM' },
  { id: 'FL', title: 'FL' },
  { id: 'GA', title: 'GA' },
  { id: 'GU', title: 'GU' },
  { id: 'HI', title: 'HI' },
  { id: 'ID', title: 'ID' },
  { id: 'IL', title: 'IL' },
  { id: 'IN', title: 'IN' },
  { id: 'IA', title: 'IA' },
  { id: 'KS', title: 'KS' },
  { id: 'KY', title: 'KY' },
  { id: 'LA', title: 'LA' },
  { id: 'ME', title: 'ME' },
  { id: 'MH', title: 'MH' },
  { id: 'MD', title: 'MD' },
  { id: 'MA', title: 'MA' },
  { id: 'MI', title: 'MI' },
  { id: 'MN', title: 'MN' },
  { id: 'MS', title: 'MS' },
  { id: 'MO', title: 'MO' },
  { id: 'MT', title: 'MT' },
  { id: 'NE', title: 'NE' },
  { id: 'NV', title: 'NV' },
  { id: 'NH', title: 'NH' },
  { id: 'NJ', title: 'NJ' },
  { id: 'NM', title: 'NM' },
  { id: 'NY', title: 'NY' },
  { id: 'NC', title: 'NC' },
  { id: 'ND', title: 'ND' },
  { id: 'MP', title: 'MP' },
  { id: 'OH', title: 'OH' },
  { id: 'OK', title: 'OK' },
  { id: 'OR', title: 'OR' },
  { id: 'PW', title: 'PW' },
  { id: 'PA', title: 'PA' },
  { id: 'PR', title: 'PR' },
  { id: 'RI', title: 'RI' },
  { id: 'SC', title: 'SC' },
  { id: 'SD', title: 'SD' },
  { id: 'TN', title: 'TN' },
  { id: 'TX', title: 'TX' },
  { id: 'UT', title: 'UT' },
  { id: 'VT', title: 'VT' },
  { id: 'VI', title: 'VI' },
  { id: 'VA', title: 'VA' },
  { id: 'WA', title: 'WA' },
  { id: 'WV', title: 'WV' },
  { id: 'WI', title: 'WI' },
  { id: 'WY', title: 'WY' },
  { id: 'Other Country', title: 'Other Country' },
];

export default function ClientEditor({ location }) {
  const { pathname, state } = location;
  const edit = pathname === '/client/edit';
  const item = state && location.state.item ? location.state.item : undefined;
  const schema = Yup.object().shape({
    financialApplication: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue.trim() === '' ? null : value;
      }),
    firstName: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.string().when('financialApplication', (field, validation) => {
            return !field && !edit
              ? validation.required('Cannot be empty')
              : validation;
          })
    ),
    middleName: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.string('Should be a valid name')
    ),
    lastName: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.string().when('financialApplication', (field, validation) =>
            !field && !edit
              ? validation.required('Cannot be empty')
              : validation
          )
    ),
    address: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.string().when('financialApplication', (field, validation) =>
            !field && !edit
              ? validation.required('Cannot be empty')
              : validation
          )
    ),
    city: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.string().when('financialApplication', (field, validation) =>
            !field && !edit
              ? validation.required('Cannot be empty')
              : validation
          )
    ),
    state: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.string()
            .oneOf(
              [null, ...states.map(stateItem => stateItem.id)],
              'Should be a valid state'
            )
            .when('financialApplication', (field, validation) =>
              !field && !edit
                ? validation.required('Cannot be empty')
                : validation
            )
    ),
    zip: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.string().when('financialApplication', (field, validation) =>
            !field && !edit
              ? validation.required('Cannot be empty')
              : validation
          )
    ),
    phoneNumber: Yup.lazy(value =>
      value === ''
        ? Yup.number().strip(true)
        : Yup.string().when('financialApplication', (field, validation) =>
            !field && !edit
              ? validation.required('Cannot be empty')
              : validation
          )
    ),
    email: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.string()
            .email('Should be a valid email')
            .when('financialApplication', (field, validation) =>
              !field && !edit
                ? validation.required('Cannot be empty')
                : validation
            )
    ),
    ssn: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.string().when('financialApplication', (field, validation) =>
            !field && !edit
              ? validation.required('Cannot be empty')
              : validation
          )
    ),
    birthday: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.date('Should be a valid date').when(
            'financialApplication',
            (field, validation) =>
              !field && !edit
                ? validation.required('Cannot be empty')
                : validation
          )
    ),
    driversLicenseNumber: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.string().when('financialApplication', (field, validation) =>
            !edit ? validation.required('Cannot be empty') : validation
          )
    ),
    driversLicenseState: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.string()
            .oneOf(
              edit
                ? [...states.map(stateItem => stateItem.id)]
                : states.map(stateItem => stateItem.id),
              'Should be a valid state'
            )
            .when('financialApplication', (field, validation) =>
              !field && !edit
                ? validation.required('Cannot be empty')
                : validation
            )
    ),
    driversLicenseExpDate: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.date('Should be a valid date').when(
            'financialApplication',
            (field, validation) =>
              !edit ? validation.required('Cannot be empty') : validation
          )
    ),
  });
  const [birthDateType, setBirthDateType] = useState('text');
  const [driversLicenseExpDateType, setDriversLicenseExpDateType] = useState(
    'text'
  );
  const handleSubmit = async (data, { resetForm }) => {
    try {
      let sendData = { ...data };
      if (!edit && item) {
        sendData = {
          ...sendData,
          financialApplication: item.id,
        };
      }
      if (edit) {
        await api.put(`/client/${item.id}`, sendData);
        toast.success(`Client was updated created.`);
      } else {
        await api.post(`/client`, sendData);
        toast.success(`Client was successfully created.`);
      }
      resetForm();
      history.push({
        pathname: '/client',
      });
    } catch (err) {
      toast.error(
        err.response &&
          err.response.data &&
          err.response.data.error &&
          typeof err.response.data.error === 'string'
          ? err.response.data.error
          : 'Error while editing the client'
      );
    }
  };

  return (
    <Container>
      <Form
        schema={schema}
        initialData={{
          financialApplication: !edit && item ? item.id : undefined,
        }}
        onSubmit={handleSubmit}
      >
        <Input hidden name="financialApplication" />
        <GroupHeader>Client Information</GroupHeader>
        <Group>
          <Input
            label="First Name"
            name="firstName"
            placeholder={item && item.firstName}
          />
          <Input
            label="Middle Initial"
            name="middleName"
            placeholder={item && item.middleName}
          />
          <Input
            label="Last Name"
            name="lastName"
            placeholder={item && item.lastName}
          />
          <Input
            label="Address"
            name="address"
            placeholder={item && item.address}
          />
          <Input label="City" name="city" placeholder={item && item.city} />
          <Select
            placeholder={item && item.state}
            label="State"
            name="state"
            options={states}
          />
          <Input placeholder={item && item.zip} label="ZIP" name="zip" />
          <Input
            label="Phone Number"
            type="number"
            name="phoneNumber"
            placeholder={item && item.phoneNumber}
          />
          <Input
            label="SSN/Passport"
            placeholder={item && item.ssn}
            name="ssn"
          />
          <Input
            label="Birth Date"
            placeholder={
              item && item.birthday ? item.birthday.split(' ')[0] : null
            }
            type={birthDateType}
            onFocus={() => {
              setBirthDateType('date');
            }}
            onBlur={() => {
              setBirthDateType('text');
            }}
            name="birthday"
          />
          <Input
            label="Driver License Number"
            name="driversLicenseNumber"
            placeholder={edit && item.driversLicenseNumber}
          />
          <Select
            placeholder={edit ? item.driversLicenseState : 'Select a State'}
            label="Driver License State"
            name="driversLicenseState"
            options={states}
          />
          <Input
            placeholder={edit && item.driversLicenseExpDate}
            label="Driver License Exp Date"
            type={driversLicenseExpDateType}
            name="driversLicenseExpDate"
            onFocus={() => {
              setDriversLicenseExpDateType('date');
            }}
            onBlur={() => {
              setDriversLicenseExpDateType('text');
            }}
          />
        </Group>
        <GroupHeader>Login Information</GroupHeader>
        <Group>
          <Input
            label="Email"
            type="email"
            name="email"
            placeholder={item && item.email}
          />
        </Group>
        <button type="submit">Create</button>
      </Form>
    </Container>
  );
}
ClientEditor.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    state: PropTypes.shape({
      item: PropTypes.shape(),
    }),
  }).isRequired,
};
